<script setup lang="ts">
import { ref, watch, nextTick, onMounted, onUnmounted, computed } from 'vue'
import { computePosition, flip, shift, offset, size, autoUpdate } from '@floating-ui/dom'
import { useCalloutStore } from '@/stores/calloutStore'

const props = defineProps<{
  id: string
  target: HTMLElement | null
  show: boolean,
  width?: string // Made optional for safety
}>()

const emit = defineEmits<{ (e: 'close'): void }>()

const calloutRef = ref<HTMLElement | null>(null)
const coords = ref({ x: 0, y: 0 })
const availableHeight = ref<number | null>(null)
const availableWidth = ref<number | null>(null)
const calloutStore = useCalloutStore()
let cleanup: (() => void) | null = null

const floatingStyles = computed(() => ({
  position: 'fixed' as const,
  top: `${coords.value.y}px`,
  left: `${coords.value.x}px`,
  minWidth: props.width || "200px",
  maxHeight: availableHeight.value ? `${availableHeight.value}px` : '90vh',
  maxWidth: availableWidth.value ? `${availableWidth.value}px` : 'calc(100vw - 16px)',
  overflowY: 'auto' as const,
  overflowX: 'auto' as const,
}))

async function updatePosition() {
  if (!props.target || !calloutRef.value) return
  
  const { x, y } = await computePosition(props.target, calloutRef.value, {
    placement: 'top',
    strategy: 'fixed',
    middleware: [
      offset(8),
      flip(),
      shift({ padding: 8 }),
      size({
        padding: 8,
        apply({ availableHeight: ah, availableWidth: aw }) {
          availableHeight.value = ah
          availableWidth.value = aw
        },
      }),
    ],
  })
  coords.value = { x, y }
}

watch(() => props.show, async (show) => {
  // Ensure previous listeners are gone
  if (cleanup) {
    cleanup()
    cleanup = null
  }

  if (show) {
    await nextTick()
    if (props.target && calloutRef.value) {
      // Floating UI's autoUpdate handles the initial call + listeners
      cleanup = autoUpdate(props.target, calloutRef.value, updatePosition)
      calloutStore.open(props.id)
    }
  } else {
    if (calloutStore.openId === props.id) {
      calloutStore.close(props.id)
    }
  }
}, { flush: 'post' }) // Ensures DOM is ready

function handleClickOutside(e: MouseEvent) {
  if (!props.show || !calloutRef.value) return

  const path = e.composedPath()
  const isClickInside = path.includes(calloutRef.value) || 
                       path.some(el => el instanceof HTMLElement && el.dataset.calloutPopup !== undefined)

  if (!isClickInside) {
    emit('close')
  }
}

onMounted(() => document.addEventListener('click', handleClickOutside))
onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  if (cleanup) cleanup()
})
</script>

<template>
  <transition name="fade">
    <div
      v-if="show"
      ref="calloutRef"
      :style="floatingStyles"
      class="callout bg-white border rounded shadow-lg p-4 z-50"
      style="touch-action: pan-y; -webkit-overflow-scrolling: touch;"
    >
      <slot />
    </div>
  </transition>
</template>